import { getUserInfo } from "@/utils/oidcUtil";
import {
    NormalizedSearchResultDto,
    QueryFilter,
    ResultDto,
    SearchResultDto,
    httpDelete,
    httpGet,
    httpPost,
    httpUpdate
} from "./claimsInvoicingApi";
import { HierarchicalDto } from "./usersApi";
import { InvoiceDetailsDto, InsertInvoiceDto, BankAccountDto, InvoiceStatus } from "@/types";
import { isFeatureFlagEnabled } from "@/utils/featureFlags";

const useSalesforceSubmit = isFeatureFlagEnabled(import.meta.env.VITE_FEATURE_SALESFORCE_SUBMIT);
export const getInvoice = async (invoiceId: string): Promise<InvoiceDetailsDto> => httpGet(`/invoices/${invoiceId}`);

export const getOcrResponse = async (docId: string): Promise<InvoiceFromOcrDto> =>
    httpGet(`/invoices/attachments/${docId}/ocrresponse`);

export const getInvoiceSender = async (crmId: string, invoiceId?: string): Promise<InvoiceSenderDto> => {
    const result: ServiceResult<InvoiceSenderDto> = await httpGet(`/invoices/invoice-sender`, {
        params: { crmId, invoiceId }
    });
    return result?.resultData;
};
export const getInvoices = async (
    filter: InvoiceQueryFilter
): Promise<NormalizedSearchResultDto<InvoiceTableDataDto>> => {
    // Build query params using URLSearchParams
    const searchParams = new URLSearchParams();

    // Handle includedStatuses as multiple IncludedStatuses params
    const { page, includedStatuses, ...rest } = filter;
    const params = {
        page: (page ?? 0) + 1,
        ...rest
    };

    if (Array.isArray(includedStatuses)) {
        for (const status of includedStatuses) {
            searchParams.append("includedStatuses", String(status));
        }
    }

    // Add other filters
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    }

    const raw: SearchResultDto<InvoiceTableDataDto> = await httpGet("/invoices", { params: searchParams });

    return {
        items: raw.items,
        returnedResults: raw.returnedResults,
        pageIndex: Math.max(0, raw.page - 1),
        totalResults: raw.totalResults
    };
};

export const getRejectedInvoices = async (
    filter: RejectedQueryFilter
): Promise<NormalizedSearchResultDto<RejectedInvoiceTableDataDto>> => {
    // Build query params using URLSearchParams
    const searchParams = new URLSearchParams();

    // Handle includedStatuses as multiple IncludedStatuses params
    const { page, ...rest } = filter;
    const params = {
        page: (page ?? 0) + 1,
        ...rest
    };

    // Add other filters
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    }

    const raw: SearchResultDto<RejectedInvoiceTableDataDto> = await httpGet("/invoices/rejected", {
        params: searchParams
    });

    return {
        items: raw.items,
        returnedResults: raw.returnedResults,
        pageIndex: Math.max(0, raw.page - 1),
        totalResults: raw.totalResults
    };
};

export const ocrScanInvoiceAttachment = async (docId: string): Promise<InvoiceFromOcrDto> =>
    httpPost(`/invoices/attachments/${docId}/ocrscan`, null);

export const createEmptyInvoice = async (crmId?: string): Promise<string> => {
    const result: ResultDto<string> = await httpPost(`/invoices/blank` + (crmId ? `/${crmId}` : ""), null);
    return result.resultData;
};

export const updateInvoice = async (id: string, invoice: InsertInvoiceDto): Promise<string> =>
    httpUpdate(`/invoices/${id}`, invoice);

export const deleteInvoice = async (id: string) => httpDelete(`/invoices/${id}`);

export const createBankAccount = async (id: string, bankAccount: BankAccountDto): Promise<BankAccountDto> =>
    httpPost(`/invoices/${id}/bankAccounts`, bankAccount);

export const createAttachment = async (id: string, form: FormData): Promise<string> => {
    const result = await httpPost(`/invoices/${id}/attachments`, form, {
        headers: { "Content-Type": null }
    });
    return String(result);
};

export const deleteAttachment = async (id: string): Promise<void> => httpDelete(`/invoices/${id}/attachments`);

export const submitInvoice = async (id: string, invoiceDetails: InsertInvoiceDto) =>
    useSalesforceSubmit
        ? httpPost(`/invoices/${id}/schedule`, null)
        : httpPost(`/invoices/${id}/submit`, invoiceDetails);

// This method is using fetch instead of Redux Toolkit Query API
// to ensure that the file is not stored in the browser cache.

export const getAttachment = async (invoiceId: string, docId?: string) => {
    if (!docId) {
        return;
    }

    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/invoices/${invoiceId}/attachments/${docId}`, {
        method: "GET",
        headers: {
            Authorization: "Bearer " + getUserInfo()?.access_token
        }
    });

    if (!response.ok) {
        throw new Error(`Attachment fetch failed for invoice ${invoiceId},
         document ${docId} with status ${response.status}`);
    }
    const documentDto: DocumentDto = await response.json();
    downloadDocument(documentDto);
};
// Max length chosen to stay well within common URL length limits
const MAX_URL_LENGTH = 1900;

// Chunk size for IDs to avoid too long URLs, chosen conservatively
// because we know 25 ids work well within the limit
const IDS_CHUNK_SIZE = 25;

// This method is using fetch instead of Redux Toolkit Query API
// to ensure that the file is not stored in the browser cache.
export const getExportedInvoices = async (invoiceIds?: string[], queryFilter?: InvoiceQueryFilter) => {
    const base = `${import.meta.env.VITE_API_BASE_URL}/invoices/export`;

    const searchParams = new URLSearchParams();
    appendInvoiceIds(searchParams, invoiceIds);
    appendQueryFilter(searchParams, queryFilter);

    const queryString = searchParams.toString();
    const url = queryString ? `${base}?${queryString}` : base;

    // If the URL is too long or we have many IDs, use chunked requests
    const ids = invoiceIds ?? [];
    const shouldChunk = url.length > MAX_URL_LENGTH;
    if (shouldChunk && ids.length > 0) {
        await exportInChunksAndDownload(base, ids, queryFilter, IDS_CHUNK_SIZE);
        return;
    }

    const response = await tryFetch(url, { method: "GET" });
    if (!response.ok) {
        throw new Error(`Invoice export failed with status ${response.status}`);
    }

    const serviceResult: ServiceResult<DocumentDto> = await response.json();
    if (!serviceResult?.resultData) {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        throw new Error("Invoice export response contained no result data");
    }

    downloadDocument(serviceResult.resultData);
};

export const getExportedRejectedInvoices = async (invoiceIds?: string[], queryFilter?: InvoiceQueryFilter) => {
    const base = `${import.meta.env.VITE_API_BASE_URL}/invoices/rejected/export`;

    const searchParams = new URLSearchParams();
    appendInvoiceIds(searchParams, invoiceIds);
    appendQueryFilter(searchParams, queryFilter);

    const queryString = searchParams.toString();
    const url = queryString ? `${base}?${queryString}` : base;

    // If the URL is too long or we have many IDs, use chunked requests
    const ids = invoiceIds ?? [];
    const shouldChunk = url.length > MAX_URL_LENGTH;
    if (shouldChunk && ids.length > 0) {
        await exportInChunksAndDownload(base, ids, queryFilter, IDS_CHUNK_SIZE);
        return;
    }

    // Normal single request path
    const response = await tryFetch(url, { method: "GET" });
    if (!response.ok) {
        throw new Error(`Invoice export failed with status ${response.status}`);
    }

    const serviceResult: ServiceResult<DocumentDto> = await response.json();
    if (!serviceResult.resultData) {
        throw new Error("Invoice export response contained no result data");
    }

    downloadDocument(serviceResult.resultData);
};

const exportInChunksAndDownload = async (
    baseUrl: string,
    ids: string[],
    queryFilter: InvoiceQueryFilter | undefined,
    chunkSize: number
) => {
    const collected: DocumentDto[] = [];
    const batches = chunkArray(ids, chunkSize);
    for (const batch of batches) {
        const searchParams = new URLSearchParams();
        appendInvoiceIds(searchParams, batch);
        appendQueryFilter(searchParams, queryFilter);
        const batchUrl = `${baseUrl}?${searchParams.toString()}`;
        const response = await tryFetch(batchUrl, { method: "GET" });
        if (!response.ok) {
            throw new Error(`Invoice export failed with status ${response.status}`);
        }
        const result: ServiceResult<DocumentDto> = await response.json();
        if (!result?.resultData) {
            throw new Error("Invoice export response contained no result data");
        }
        collected.push(result.resultData);
    }

    const allCsv = collected.every(d => (d.fileExtension ?? "").toLowerCase() === "csv");
    if (allCsv && collected.length > 0) {
        const merged = mergeCsvDocuments(collected);
        downloadDocument(merged);
        return;
    }
    for (const doc of collected) {
        downloadDocument(doc);
    }
};

const safeFilename = (name?: string, ext?: string) => {
    const base = (name ?? "download").replaceAll(/[\0\n\r\t\\/:*?"<>|]+/g, "_").trim() || "download";

    const cleanedExt = (ext ?? "")
        .replaceAll(/[^a-zA-Z0-9.+-]+/g, "")
        .toLowerCase()
        .replace(/^\./, "");

    return cleanedExt ? `${base}.${cleanedExt}` : base;
};

const downloadDocument = (documentDto: DocumentDto) => {
    const fileBlob = b64toBlob(documentDto.content, documentDto.fileExtension);
    const objectUrl = globalThis.URL.createObjectURL(fileBlob);
    const link = globalThis.document.createElement("a");
    link.href = objectUrl;
    link.download = safeFilename(documentDto.filename, documentDto.fileExtension);
    globalThis.document.body.appendChild(link);
    link.click();
    link.remove();

    const URL_REVOKE_DELAY_MS = 100;
    setTimeout(() => {
        globalThis.URL.revokeObjectURL(objectUrl);
    }, URL_REVOKE_DELAY_MS);
};

const chunkArray = <T>(array: T[] | undefined | null, chunkSize: number): T[][] => {
    if (!array || array.length === 0) {
        return [];
    }
    if (!Number.isFinite(chunkSize) || chunkSize < 1) {
        throw new Error(`chunkArray: invalid chunkSize ${chunkSize}`);
    }

    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
};

// Helpers to merge multiple CSV DocumentDto into one
const b64ToText = (b64: string): string => atob(b64);
const textToB64 = (text: string): string => btoa(text);

const mergeCsvDocuments = (documents: DocumentDto[]): DocumentDto => {
    const texts = documents.map(d => b64ToText(d.content));
    const [firstText, ...restTexts] = texts;
    const firstLines = (firstText ?? "").split(/\r?\n/);
    const header = firstLines[0] ?? "";
    const mergedBodies: string[] = [];
    const firstBody = firstLines
        .slice(1)
        .filter(l => l.length > 0)
        .join("\n");
    if (firstBody) {
        mergedBodies.push(firstBody);
    }
    for (const txt of restTexts) {
        const lines = (txt ?? "").split(/\r?\n/);
        const body = lines
            .slice(1)
            .filter(line => line.length > 0)
            .join("\n");
        if (body) {
            mergedBodies.push(body);
        }
    }
    const mergedText = header ? [header, ...mergedBodies].join("\n") : mergedBodies.join("\n");
    const baseName = documents[0]?.filename?.trim() || "invoices";
    return {
        content: textToB64(mergedText),
        filename: baseName,
        fileExtension: "csv"
    };
};

const tryFetch = (url: string, init?: RequestInit): Promise<Response> => {
    const { headers, ...rest } = init ?? {};
    return fetch(url, {
        cache: "no-store",
        headers: (() => {
            const baseHeaders: Record<string, string> = {
                Accept: "application/json",
                Authorization: "Bearer " + (getUserInfo()?.access_token ?? "")
            };
            return headers ? { ...baseHeaders, ...(headers as Record<string, string>) } : baseHeaders;
        })(),
        ...rest
    });
};

const normalizeFilterValue = (key: string, raw: unknown): string | undefined => {
    if (raw == null || raw === "" || typeof raw === "object") {
        return undefined;
    }
    if (key === "page" && typeof raw === "number") {
        return String(raw + 1);
    }

    if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
        return String(raw);
    }

    return undefined;
};

const appendInvoiceIds = (searchParams: URLSearchParams, invoiceIds?: string[]) => {
    if (!invoiceIds?.length) {
        return;
    }
    for (const id of invoiceIds) {
        searchParams.append("invoiceIds", id);
    }
};

const appendQueryFilter = (searchParams: URLSearchParams, queryFilter?: InvoiceQueryFilter) => {
    if (!queryFilter) {
        return;
    }

    if (Array.isArray(queryFilter.includedStatuses)) {
        for (const status of queryFilter.includedStatuses) {
            searchParams.append("includedStatuses", String(status));
        }
    }

    for (const [key, raw] of Object.entries(queryFilter)) {
        const value = normalizeFilterValue(key, raw);
        if (value !== undefined) {
            searchParams.append(key, value);
        }
    }
};

export type InvoiceFromOcrDto = {
    id: string;
    companyCrmId: string;
    invoiceDate: string;
    invoiceNumber: string;
    claimsRef: string;
    subClaimsRef: string;
    correspondentCosts: number;
    surveyorCosts: number;
    expertCosts: number;
    lawyerFees: number;
    lawyerExpenses: number;
    totalVatAmount: number;
    totalLocalTax: number;
    clientVat: string;
    accountId: string;
    vesselName: string;
    incidentDate: string;
    clientName: string;
    otherExpenses: number;
    advanceOfFunds: number;
    previousAdvanceOfFunds: number;
    invoiceDueDate: string;
    totalCosts: number;
    bankAccount: string;
    currency: string;
    vatNumber: string | undefined;
};

export type InvoiceTableDataDto = {
    id: string;
    invoiceDate: string;
    invoiceDueDate: string;
    invoiceReference: string;
    subClaimsRef: string;
    incidentDate: string;
    vessel: string;
    status: string;
    submittedDate?: string;
    totalInvoiceAmount: string;
    rejectedReason: string;
    hierarchy: HierarchicalDto;
    branchCompany: string;
    claimsHandlerEmail: string;
    claimsHandlerName: string;
};

export type RejectedInvoiceTableDataDto = {
    id: string;
    invoiceDate: string;
    invoiceReference: string;
    claimsRef: string;
    subClaimRef: string;
    status: string;
    submittedDate?: string;
    totalInvoiceAmount: number;
    currency: string;
    rejectedReason: string;
    rejectedDate: string;
};

type ServiceResult<T> = {
    resultCode: string;
    description: string;
    resultData: T;
};

type DocumentDto = {
    content: string;
    filename: string;
    fileExtension: string;
};

export type InvoiceQueryFilter = QueryFilter & {
    includedStatuses?: InvoiceStatus[];
};

export type RejectedQueryFilter = QueryFilter & {
    rejectedFromDate?: string;
    rejectedToDate?: string;
};

export type InvoiceSenderDto = {
    id: string;
    name: string;
    bankAccounts: BankAccountDto[];
};

const SLICE_SIZE = 512;

const b64toBlob = (b64Data: string, contentType = "", sliceSize = SLICE_SIZE) => {
    const byteCharacters = atob(b64Data);
    const byteArrays: BlobPart[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = Uint8Array.from(slice, char => char.codePointAt(0) ?? 0);
        byteArrays.push(byteNumbers);
    }

    return new Blob(byteArrays, { type: contentType });
};
